import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaStar, FaShieldAlt, FaGift, FaClock, FaUsers, FaChartLine,
  FaArrowRight, FaCheckCircle, FaTrophy, FaLock, FaGamepad,
  FaCreditCard, FaMobileAlt, FaVideo, FaCoins, FaNewspaper,
  FaAward, FaHandshake, FaRocket, FaDice, FaSpinner, FaSearch
} from 'react-icons/fa';
import SEO from '../components/SEO';

const Home: React.FC = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const offers = [
    {
      id: 'allbritishcasino',
      name: 'All British Casino',
      image: '/images/offers/allbritishcasino.webp',
      bonus: '100% up to £100',
      rating: 4.8,
      features: ['UK Licensed', 'Fast Payouts', 'Live Casino'],
      url: 'https://www.allbritishcasino.com/'
    },
    {
      id: 'funcasino',
      name: 'Fun Casino',
      image: '/images/offers/funcasino.webp',
      bonus: '200% up to £200',
      rating: 4.7,
      features: ['Exclusive Games', 'VIP Program', '24/7 Support'],
      url: 'https://www.funcasino.com/'
    },
    {
      id: 'kwiff',
      name: 'Kwiff',
      image: '/images/offers/kwiff.webp',
      bonus: '£30 Free Bet',
      rating: 4.9,
      features: ['Surprise Bets', 'Mobile App', 'Quick Withdrawals'],
      url: 'https://www.kwiff.com/'
    }
  ];

  const stats = [
    { icon: FaUsers, value: '50K+', label: 'Active Players' },
    { icon: FaGift, value: '£2M+', label: 'Bonuses Claimed' },
    { icon: FaStar, value: '4.8', label: 'Average Rating' },
    { icon: FaShieldAlt, value: '100%', label: 'Licensed Casinos' }
  ];

  return (
    <>
      <SEO
        title="UK Casino Reviews & Exclusive Offers"
        description="Discover the best UK casino offers with comprehensive reviews, exclusive bonuses, and expert comparisons. Find your perfect casino match today."
        schema={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'SpinHarvester',
          url: 'https://spinharvester.com',
          description: 'Comprehensive UK casino reviews and exclusive offers',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://spinharvester.com/search?q={search_term_string}',
            'query-input': 'required name=search_term_string'
          }
        }}
      />

      {/* Hero Section with 6 Anchor Links */}
      <section className="relative bg-gradient-to-br from-primary-50 via-white to-primary-50 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Find Your Perfect
                <span className="text-primary-600 block">UK Casino Match</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Unlock exclusive bonuses, read expert reviews, and compare the top UK casinos. Your next big win starts here.
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-8">
                <a href="#top-offers" className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg">
                  View Top Offers
                </a>
                <a href="#comparison" className="bg-white hover:bg-gray-50 text-primary-600 border-2 border-primary-600 px-8 py-4 rounded-lg font-semibold transition-all duration-200">
                  Compare Casinos
                </a>
              </div>
              <div className="flex flex-wrap justify-center lg:justify-start gap-6 text-sm">
                <a href="#reviews" className="text-gray-600 hover:text-primary-600 transition-colors flex items-center gap-2">
                  <FaStar className="text-primary-500" /> Expert Reviews
                </a>
                <a href="#bonuses" className="text-gray-600 hover:text-primary-600 transition-colors flex items-center gap-2">
                  <FaGift className="text-primary-500" /> Exclusive Bonuses
                </a>
                <a href="#safety" className="text-gray-600 hover:text-primary-600 transition-colors flex items-center gap-2">
                  <FaShieldAlt className="text-primary-500" /> Safety Guide
                </a>
                <a href="#guides" className="text-gray-600 hover:text-primary-600 transition-colors flex items-center gap-2">
                  <FaChartLine className="text-primary-500" /> Strategy Guides
                </a>
                <a href="#faq" className="text-gray-600 hover:text-primary-600 transition-colors flex items-center gap-2">
                  <FaUsers className="text-primary-500" /> FAQ
                </a>
                <a href="#testimonials" className="text-gray-600 hover:text-primary-600 transition-colors flex items-center gap-2">
                  <FaTrophy className="text-primary-500" /> Player Stories
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4 relative z-10">
                {/* Poker Cards Image */}
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-primary-400 to-primary-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-300"></div>
                  <div className="relative">
                    <img 
                      src="/images/hero1.webp" 
                      alt="Poker Cards and Chips" 
                      className="rounded-2xl shadow-2xl w-full h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="eager"
                      width="300"
                      height="320"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-2xl"></div>
                  </div>
                </div>
                {/* Slot Machines Image */}
                <div className="relative group mt-8">
                  <div className="absolute -inset-1 bg-gradient-to-r from-primary-400 to-primary-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-300"></div>
                  <div className="relative">
                    <img 
                      src="/images/hero2.webp" 
                      alt="Slot Machines" 
                      className="rounded-2xl shadow-2xl w-full h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="eager"
                      width="300"
                      height="320"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-primary-600 text-white p-6 rounded-xl shadow-xl z-20">
                <div className="text-3xl font-bold">4.8/5</div>
                <div className="text-sm">Average Rating</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="text-4xl text-primary-600 mx-auto mb-4" />
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Offers Section */}
      <section id="top-offers" className="bg-gray-50 py-20 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Top Casino Offers</h2>
            <p className="text-xl text-gray-600">Handpicked exclusive bonuses just for you</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {offers.map((offer) => (
              <div key={offer.id} className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={offer.image} 
                    alt={offer.name}
                    className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-4 right-4 bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {offer.bonus}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-gray-900">{offer.name}</h3>
                    <div className="flex items-center gap-1">
                      <FaStar className="text-yellow-400" />
                      <span className="font-semibold">{offer.rating}</span>
                    </div>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {offer.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-gray-600">
                        <FaCheckCircle className="text-primary-600 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-3">
                    <a
                      href={offer.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-primary-600 hover:bg-primary-700 text-white text-center py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center gap-2"
                    >
                      Claim Bonus <FaArrowRight />
                    </a>
                    <Link
                      to={`/offers/${offer.id}`}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-900 text-center py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center gap-2"
                    >
                      Review
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section id="comparison" className="bg-white py-20 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Compare Casino Offers</h2>
            <p className="text-xl text-gray-600 mb-8">Side-by-side comparison of the best UK casinos</p>
            <Link
              to="/comparison"
              className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
            >
              View Full Comparison <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="bg-gray-50 py-20 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Expert Casino Reviews</h2>
            <p className="text-xl text-gray-600">In-depth analysis from our expert team</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-primary-100 p-3 rounded-lg">
                  <FaShieldAlt className="text-2xl text-primary-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Licensing & Safety</h3>
                  <p className="text-gray-600">We verify every casino's UK Gambling Commission license</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-primary-100 p-3 rounded-lg">
                  <FaClock className="text-2xl text-primary-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Payout Speed</h3>
                  <p className="text-gray-600">Real withdrawal times tested by our team</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-primary-100 p-3 rounded-lg">
                  <FaGamepad className="text-2xl text-primary-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Game Selection</h3>
                  <p className="text-gray-600">Comprehensive game library analysis</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-primary-100 p-3 rounded-lg">
                  <FaGift className="text-2xl text-primary-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Bonus Terms</h3>
                  <p className="text-gray-600">Clear breakdown of wagering requirements</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section id="bonuses" className="bg-white py-20 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Exclusive Bonuses</h2>
            <p className="text-xl text-gray-600">Special offers you won't find anywhere else</p>
          </div>
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 text-white">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <FaGift className="text-4xl mb-4" />
                <h3 className="text-2xl font-bold mb-2">Welcome Bonuses</h3>
                <p>Double your first deposit with our exclusive welcome offers</p>
              </div>
              <div>
                <FaTrophy className="text-4xl mb-4" />
                <h3 className="text-2xl font-bold mb-2">VIP Rewards</h3>
                <p>Access to exclusive VIP programs and cashback deals</p>
              </div>
              <div>
                <FaStar className="text-4xl mb-4" />
                <h3 className="text-2xl font-bold mb-2">Free Spins</h3>
                <p>Regular free spins offers on the latest slot releases</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Safety Guide */}
      <section id="safety" className="bg-gray-50 py-20 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Safe Gambling Guide</h2>
            <p className="text-xl text-gray-600">Your safety is our priority</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <FaLock className="text-4xl text-primary-600 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Licensed Operators Only</h3>
              <p className="text-gray-600 mb-4">
                We only review casinos licensed by the UK Gambling Commission. Every operator on our site has been thoroughly vetted for security and fairness.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <FaShieldAlt className="text-4xl text-primary-600 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Responsible Gambling</h3>
              <p className="text-gray-600 mb-4">
                We promote responsible gambling and provide resources for players who need support. Always gamble within your means.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Strategy Guides */}
      <section id="guides" className="bg-white py-20 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Strategy Guides</h2>
            <p className="text-xl text-gray-600">Expert tips to maximize your casino experience</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {['Bankroll Management', 'Bonus Optimization', 'Game Selection'].map((guide, idx) => (
              <div key={idx} className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow duration-300">
                <FaChartLine className="text-3xl text-primary-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">{guide}</h3>
                <p className="text-gray-600">Learn proven strategies to enhance your gameplay and maximize your winnings.</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section id="faq" className="bg-gray-50 py-20 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 mb-8">Quick answers to common questions</p>
          </div>
          <div className="space-y-4">
            {[
              { q: 'Are these casinos safe?', a: 'Yes, all casinos we review are licensed by the UK Gambling Commission.' },
              { q: 'How do I claim bonuses?', a: 'Click through our links and register with the casino to claim exclusive offers.' }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow">
                <h3 className="font-bold text-gray-900 mb-2">{item.q}</h3>
                <p className="text-gray-600">{item.a}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-semibold"
            >
              View All FAQs <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="bg-white py-20 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Player Success Stories</h2>
            <p className="text-xl text-gray-600">Real experiences from our community</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: 'Sarah M.', text: 'Found the perfect casino through SpinHarvester. The bonus was exactly as described!' },
              { name: 'James T.', text: 'The comparison tool helped me choose between offers. Great resource!' },
              { name: 'Emma L.', text: 'Fast payouts and excellent customer service. Thanks for the recommendation!' }
            ].map((testimonial, idx) => (
              <div key={idx} className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">"{testimonial.text}"</p>
                <p className="font-semibold text-gray-900">— {testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Game Categories */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Popular Game Categories</h2>
            <p className="text-xl text-gray-600">Explore the best games across all categories</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {[
              { icon: FaSpinner, name: 'Slots', count: '5000+' },
              { icon: FaDice, name: 'Table Games', count: '200+' },
              { icon: FaVideo, name: 'Live Casino', count: '150+' },
              { icon: FaCoins, name: 'Jackpots', count: '100+' },
              { icon: FaGamepad, name: 'Video Poker', count: '50+' },
              { icon: FaTrophy, name: 'Tournaments', count: 'Daily' }
            ].map((category, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 text-center group cursor-pointer">
                <category.icon className="text-4xl text-primary-600 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-bold text-gray-900 mb-1">{category.name}</h3>
                <p className="text-sm text-gray-600">{category.count}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Methods */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Secure Payment Methods</h2>
            <p className="text-xl text-gray-600">Fast, secure, and trusted payment options</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {['Visa', 'Mastercard', 'PayPal', 'Skrill', 'Neteller', 'Bank Transfer'].map((method, idx) => (
              <div key={idx} className="bg-gray-50 p-6 rounded-xl text-center hover:bg-primary-50 transition-colors">
                <FaCreditCard className="text-3xl text-primary-600 mx-auto mb-3" />
                <p className="font-semibold text-gray-900">{method}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-gray-600">All payment methods are encrypted and secure</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-gradient-to-br from-primary-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">Get started in three simple steps</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Browse & Compare', desc: 'Explore our curated list of UK-licensed casinos and compare offers side by side.', icon: FaSearch },
              { step: '02', title: 'Read Expert Reviews', desc: 'Get detailed insights on bonuses, games, payout speeds, and customer service.', icon: FaStar },
              { step: '03', title: 'Claim Your Bonus', desc: 'Click through our exclusive links to claim your welcome bonus and start playing.', icon: FaGift }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-8 rounded-2xl shadow-lg relative">
                <div className="absolute -top-6 left-8 bg-primary-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl">
                  {item.step}
                </div>
                <item.icon className="text-5xl text-primary-600 mb-6 mt-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Gaming */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Play On The Go</h2>
              <p className="text-xl text-gray-600 mb-6">
                All recommended casinos offer fully optimized mobile experiences. Play your favorite games anywhere, anytime.
              </p>
              <ul className="space-y-4 mb-8">
                {['iOS & Android Apps', 'Instant Play Browser', 'Mobile-Optimized Games', 'Touch-Friendly Interface'].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <FaCheckCircle className="text-primary-600 text-xl flex-shrink-0" />
                    <span className="text-gray-700 text-lg">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-primary-100 to-primary-200 p-12 rounded-2xl text-center">
              <img src="/images/mobile.webp" alt="Mobile Gaming" className="w-full h-full object-contain" />
            </div>
          </div>
        </div>
      </section>

      {/* Live Casino */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Live Casino Experience</h2>
            <p className="text-xl text-gray-600">Real dealers, real action, real wins</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Live Blackjack', providers: 'Evolution, Playtech', games: '50+' },
              { title: 'Live Roulette', providers: 'NetEnt, Pragmatic', games: '30+' },
              { title: 'Live Baccarat', providers: 'Ezugi, Vivo Gaming', games: '25+' }
            ].map((game, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-shadow">
                <FaVideo className="text-4xl text-primary-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">{game.title}</h3>
                <p className="text-gray-600 mb-1"><strong>Providers:</strong> {game.providers}</p>
                <p className="text-gray-600"><strong>Games:</strong> {game.games}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Jackpots */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-700 py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <FaCoins className="text-6xl mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-4">Mega Jackpots</h2>
            <p className="text-xl opacity-90">Life-changing wins await</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { amount: '£2.5M', game: 'Mega Moolah', casino: 'Multiple Casinos' },
              { amount: '£1.8M', game: 'Major Millions', casino: 'Top Rated' },
              { amount: '£950K', game: 'King Cashalot', casino: 'Exclusive' }
            ].map((jackpot, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <div className="text-3xl font-bold mb-2">{jackpot.amount}</div>
                <div className="text-lg mb-1">{jackpot.game}</div>
                <div className="text-sm opacity-80">{jackpot.casino}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Trust SpinHarvester?</h2>
            <p className="text-xl text-gray-600">We're committed to transparency and player safety</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: FaAward, title: 'Expert Reviews', desc: 'In-depth analysis by gambling experts' },
              { icon: FaShieldAlt, title: 'Licensed Only', desc: 'UKGC licensed casinos exclusively' },
              { icon: FaHandshake, title: 'Unbiased', desc: 'Honest reviews, no hidden agendas' },
              { icon: FaClock, title: 'Updated Daily', desc: 'Fresh content and latest offers' }
            ].map((badge, idx) => (
              <div key={idx} className="text-center">
                <badge.icon className="text-5xl text-primary-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">{badge.title}</h3>
                <p className="text-gray-600">{badge.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* New Casinos */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <FaRocket className="text-5xl text-primary-600 mx-auto mb-4" />
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Newest Casino Additions</h2>
            <p className="text-xl text-gray-600">Fresh casinos with exciting welcome offers</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: 'Lucky Spins Casino', bonus: '200% + 100 Spins', rating: 4.9 },
              { name: 'Royal Vegas UK', bonus: '£500 Welcome', rating: 4.8 },
              { name: 'Elite Gaming Club', bonus: '150% + 50 Spins', rating: 4.7 }
            ].map((casino, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">{casino.name}</h3>
                  <div className="flex items-center gap-1">
                    <FaStar className="text-yellow-400" />
                    <span className="font-semibold">{casino.rating}</span>
                  </div>
                </div>
                <div className="bg-primary-50 p-4 rounded-lg mb-4">
                  <p className="text-primary-600 font-semibold">{casino.bonus}</p>
                </div>
                <button className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-semibold transition-colors">
                  View Details
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Best Rated */}
      <section className="bg-white py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <FaTrophy className="text-4xl md:text-5xl text-primary-600 mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 md:mb-4">Top Rated Casinos</h2>
            <p className="text-lg md:text-xl text-gray-600">Highest rated by our expert team</p>
          </div>
          <div className="space-y-4 md:space-y-4">
            {[
              { rank: 1, name: 'Kwiff', rating: 4.9, bonus: '£30 Free Bet', features: ['Surprise Bets', 'Fast Payouts', 'Mobile App'] },
              { rank: 2, name: 'All British Casino', rating: 4.8, bonus: '100% up to £100', features: ['UK Licensed', 'Live Casino', '24/7 Support'] },
              { rank: 3, name: 'Fun Casino', rating: 4.7, bonus: '200% up to £200', features: ['VIP Program', 'Exclusive Games', 'Quick Withdrawals'] }
            ].map((casino, idx) => (
              <div key={idx} className="bg-gray-50 p-4 md:p-6 rounded-xl hover:shadow-lg transition-shadow flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                <div className="flex items-center gap-4 md:block">
                  <div className="bg-primary-600 text-white w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center font-bold text-xl md:text-2xl flex-shrink-0">
                    {casino.rank}
                  </div>
                  <div className="md:hidden">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-xl font-bold text-gray-900">{casino.name}</h3>
                      <div className="flex items-center gap-1">
                        <FaStar className="text-yellow-400 text-sm" />
                        <span className="text-lg font-bold">{casino.rating}</span>
                      </div>
                    </div>
                    <p className="text-primary-600 font-semibold text-sm">{casino.bonus}</p>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="hidden md:flex items-center justify-between mb-2">
                    <h3 className="text-2xl font-bold text-gray-900">{casino.name}</h3>
                    <div className="flex items-center gap-2">
                      <FaStar className="text-yellow-400" />
                      <span className="text-xl font-bold">{casino.rating}</span>
                    </div>
                  </div>
                  <p className="hidden md:block text-primary-600 font-semibold mb-2">{casino.bonus}</p>
                  <div className="flex flex-wrap gap-2">
                    {casino.features.map((feature, fIdx) => (
                      <span key={fIdx} className="bg-white px-2 py-1 md:px-3 md:py-1 rounded-full text-xs md:text-sm text-gray-700">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
                <Link
                  to={`/offers/${casino.name.toLowerCase().replace(/\s+/g, '')}`}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 md:px-6 md:py-3 rounded-lg font-semibold transition-colors flex-shrink-0 text-center text-sm md:text-base w-full md:w-auto"
                >
                  View Review
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;


